import React, { useState } from 'react';
import { GripVertical, Info, Maximize2, Minimize2, Atom, Activity, Zap, Box, Target, ChevronRight, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SimulationProvider } from '../context/SimulationContext';
import { MATERIALS } from '../data/physicsConstants';
import Scene3D from '../components/simulation/Scene3D';
import ControlPanel from '../components/simulation/ControlPanel';
import ResultsModal from '../components/simulation/ResultsModal';
import ControlRoomModal from '../components/simulation/ControlRoomModal';
import { useSimulation } from '../hooks/useSimulation';
import { presets as PRESETS_DATA } from '../data/presets.json';

// ============================================
// PREMIUM STAT CARD
// ============================================
const StatCard = ({ label, value, unit, color = "text-white", icon: Icon }) => (
   <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-all hover:bg-white/[0.15]"
   >
      <div className="flex items-center gap-2 mb-2">
         {Icon && <Icon size={14} className="text-white/50" />}
         <span className="text-[10px] uppercase tracking-wider text-white/50 font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
         <span className={`text-2xl font-bold font-mono ${color}`}>{value}</span>
         {unit && <span className="text-xs text-white/40">{unit}</span>}
      </div>
   </motion.div>
);

// ============================================
// MATERIAL INDICATOR PILL
// ============================================
const MaterialPill = ({ material, color }) => (
   <div className="flex items-center gap-2 bg-white/10 backdrop-blur-xl px-4 py-2 rounded-full border border-white/10">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-sm font-semibold text-white">{material}</span>
   </div>
);

// ============================================
// INNER SIMULATOR CONTENT
// ============================================
const SimulatorContent = () => {
   const { state, actions, isRunning, results } = useSimulation();
   const [isSidebarOpen, setIsSidebarOpen] = useState(true);
   const [isResultsOpen, setIsResultsOpen] = useState(false);

   // Find active preset data
   const activePresetData = state.activePreset
      ? PRESETS_DATA.find(p => p.id === state.activePreset)
      : null;

       const headerTitle = activePresetData ? activePresetData.name : "Particle Simulator";   const headerSubtitle = activePresetData ? "Preset Experiment" : "MCS Tomography";


   const mat = MATERIALS[state.material] || MATERIALS.pvc;

   return (
      <div className="relative flex h-screen overflow-hidden bg-[#0a0a0a]">

         {/* Left Panel: 3D Scene */}
         <div className={`relative transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] flex-1 min-w-0`}>

            {/* Premium Gradient Overlay - Top */}
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 via-black/20 to-transparent z-10 pointer-events-none" />

            {/* Premium Gradient Overlay - Bottom */}
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10 pointer-events-none" />

            {/* Header Badge */}
            <div className="absolute top-6 left-6 z-20">
               <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 bg-black/40 backdrop-blur-xl rounded-2xl px-5 py-3 border border-white/10">
                     <Atom size={24} className="text-white" />
                     <div>
                        <h1 className="text-lg font-bold text-white tracking-tight">{headerTitle}</h1>
                        <p className="text-[10px] uppercase tracking-widest text-white/50">{headerSubtitle}</p>
                     </div>
                  </div>

                  {/* Status Indicator */}
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-xl border ${isRunning ? 'bg-green-500/20 border-green-500/30' : results ? 'bg-blue-500/20 border-blue-500/30' : 'bg-white/10 border-white/10'}`}>
                     <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-400 animate-pulse' : results ? 'bg-blue-400' : 'bg-white/50'}`} />
                     <span className={`text-xs font-medium ${isRunning ? 'text-green-300' : results ? 'text-blue-300' : 'text-white/70'}`}>
                        {isRunning ? 'SIMULATING' : results ? 'COMPLETE' : 'READY'}
                     </span>
                  </div>
               </div>
            </div>





            {/* The 3D Scene */}
            <Scene3D isResultsOpen={isResultsOpen} />

            {/* Bottom Stats Panel */}
            <div className="absolute bottom-6 left-6 right-6 z-20">
               <div className="flex items-end justify-between gap-6">

                  {/* Material & Quick Stats */}
                  <div className="space-y-4">
                     <MaterialPill material={mat.name.split(' ')[0]} color={mat.color} />

                     <div className="grid grid-cols-4 gap-3">
                        <StatCard
                           label="Momentum"
                           value={state.beam.momentum.toFixed(2)}
                           unit="GeV/c"
                           icon={Zap}
                           color="text-cyan-400"
                        />
                        <StatCard
                           label="θ RMS"
                           value={state.derived.mcsAngle?.toFixed(2) || '-'}
                           unit="mrad"
                           icon={Activity}
                           color="text-orange-400"
                        />
                        <StatCard
                           label="X₀"
                           value={mat.X0}
                           unit="cm"
                           icon={Box}
                           color="text-purple-400"
                        />
                        <StatCard
                           label="Thickness"
                           value={state.phantomThickness}
                           unit="cm"
                           icon={Target}
                           color="text-indigo-400"
                        />
                     </div>
                  </div>

                  {/* Live Feed (when running) */}
                  <AnimatePresence>
                     {isRunning && (
                        <motion.div
                           initial={{ opacity: 0, x: 20 }}
                           animate={{ opacity: 1, x: 0 }}
                           exit={{ opacity: 0, x: 20 }}
                           className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10 w-64"
                        >
                           <div className="flex items-center gap-2 mb-3">
                              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                              <span className="text-[10px] uppercase tracking-wider text-white/50 font-medium">Live Tracker Feed</span>
                           </div>
                           <div className="space-y-1.5 font-mono text-xs">
                              <div className="flex justify-between">
                                 <span className="text-white/40">Upstream 1:</span>
                                 <span className="text-green-400">━━━━━━</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-white/40">Upstream 2:</span>
                                 <span className="text-green-300">━━━━━</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-white/40">Downstream 1:</span>
                                 <span className="text-blue-400">━━━━</span>
                              </div>
                              <div className="flex justify-between">
                                 <span className="text-white/40">Downstream 2:</span>
                                 <span className="text-orange-400">━━━</span>
                              </div>
                           </div>
                        </motion.div>
                     )}
                  </AnimatePresence>
               </div>
            </div>

            {/* Corner Decorations */}
            <div className="absolute top-20 left-6 w-px h-20 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
            <div className="absolute top-20 left-6 w-20 h-px bg-gradient-to-r from-white/20 to-transparent pointer-events-none" />
         </div>

         {/* Divider */}
         <div className="w-px bg-white/10 hover:bg-white/20 cursor-col-resize flex items-center justify-center transition-colors z-30 group">
            <GripVertical size={12} className="text-white/30 group-hover:text-white/60" />
         </div>

         {/* Sidebar Toggle Button (Moved to Main Container) */}
         <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`absolute top-6 z-[1000] cursor-pointer pointer-events-auto group flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-md border border-white/10 rounded-xl hover:bg-white/20 hover:scale-105 active:scale-95 transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] shadow-lg ${isSidebarOpen ? 'right-6 md:right-[444px]' : 'right-6'}`}
            title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
         >
            {isSidebarOpen ? <PanelRightClose size={18} className="text-white/80 group-hover:text-white" /> : <PanelRightOpen size={18} className="text-white/80 group-hover:text-white" />}
         </button>

         {/* Right Panel: Controls */}
         <div className={`h-full z-[60] transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${!isSidebarOpen ? 'w-0 opacity-0 overflow-hidden pointer-events-none' : 'w-full md:w-[420px] min-w-[360px] max-w-[500px] opacity-100'} absolute right-0 md:relative`}>
            <ControlPanel
               onShowResults={() => setIsResultsOpen(true)}
               onOpenControlRoom={() => actions.setControlRoomOpen(true)}
            />
         </div>

         {/* Results Modal */}
         <ResultsModal
            isOpen={isResultsOpen}
            onClose={() => setIsResultsOpen(false)}
         />

         {/* Control Room Modal */}
         <ControlRoomModal
            isOpen={state.controlRoomOpen}
            onClose={() => actions.setControlRoomOpen(false)}
         />
      </div>
   );
};

// Main Simulator component wraps with Provider
const Simulator = () => {
   return (
      <SimulationProvider>
         <SimulatorContent />
      </SimulationProvider>
   );
};

export default Simulator;

